import { useFileConstraints } from '../features/use-file-constraints';
import { useCommentConstraints } from '../features/use-comment-constraints';
import { useFeatureChecks } from '../features/feature-context';

/**
 * Hook to get form configuration based on feature toggles
 * Useful for initializing forms with feature-aware settings
 */
export const useFormConfig = () => {
    const fileConstraints = useFileConstraints();
    const commentConstraints = useCommentConstraints();
    const { useFeature } = useFeatureChecks();

    return {
        files: {
            ...fileConstraints,
            // Additional file-related helpers
            isUploadEnabled: fileConstraints.allowedFileTypes,
            getSizeHelpText: () => `Maximum ${fileConstraints.maxSizeMb}MB per file`,
            getTypeHelpText: () =>
                `Allowed types: ${fileConstraints.allowedExts?.join(', ') ?? ''}`,
        },
        comments: {
            ...commentConstraints,
            // Additional comment-related helpers
            getHelpText: (currentLength = 0) =>
                `${commentConstraints.maxLength - currentLength} characters remaining`,
            isAtLimit: (currentLength = 0) => currentLength >= commentConstraints.maxLength,
        },
        features: {
            canSelfReview: useFeature('evidence.canSelfReview', false),
            // Add other feature flags as needed
        },
    };
};

/**
 * Utility to validate file constraints without Yup
 * Useful for real-time validation in UI components
 */
export const validateFileConstraints = (files, constraints) => {
    const { maxBytes, allowedMimes, allowedExts } = constraints;
    const errors = [];

    if (!Array.isArray(files)) {
        files = files ? [files] : [];
    }

    for (const file of files) {
        // Size validation
        if (file.size > maxBytes) {
            errors.push({
                file: file.name,
                type: 'size',
                message: `File "${file.name}" exceeds maximum size of ${Math.floor(
                    maxBytes / (1024 * 1024)
                )}MB`,
            });
        }

        // Type validation
        const isValidType =
            allowedMimes?.includes(file.type) ||
            allowedExts?.some((ext) => file.name?.toLowerCase()?.endsWith(ext));

        if (!isValidType) {
            errors.push({
                file: file.name,
                type: 'type',
                message: `File "${file.name}" has unsupported type. Allowed: ${allowedExts?.join(
                    ', '
                )}`,
            });
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    };
};
